/**
 * All admin routes (SPEC-FRONTEND §7). Declared once in phase 01 and frozen afterwards:
 * feature agents implement the lazily loaded pages in `features/<f>/pages/`.
 */
import type { ComponentType } from 'react';
import { createBrowserRouter, Outlet, type RouteObject } from 'react-router';

import { GuestOnly } from '@/auth/GuestOnly';
import { RequireAuth } from '@/auth/RequireAuth';
import { ROUTER_BASENAME } from '@/basename';
import { FullPageLoader } from '@/components/FullPage';
import { AdminShell } from '@/layout/AdminShell';
import { NotFoundPage } from '@/layout/NotFoundPage';
import { RouteErrorBoundary } from '@/layout/RouteErrorBoundary';

/** Route-level code splitting: every page is a default export in its own chunk. */
function page(load: () => Promise<{ default: ComponentType }>) {
  return async () => ({ Component: (await load()).default });
}

const routes: RouteObject[] = [
  {
    path: '/',
    HydrateFallback: FullPageLoader,
    errorElement: <RouteErrorBoundary fullPage />,
    children: [
      {
        path: 'login',
        element: (
          <GuestOnly>
            <Outlet />
          </GuestOnly>
        ),
        children: [{ index: true, lazy: page(() => import('@/features/auth/pages/LoginPage')) }],
      },
      {
        element: (
          <RequireAuth>
            <AdminShell />
          </RequireAuth>
        ),
        children: [
          {
            // Page-level boundary: a crashing screen keeps the shell and navigation usable.
            errorElement: <RouteErrorBoundary />,
            children: [
              { index: true, lazy: page(() => import('@/features/dashboard/pages/DashboardPage')) },

              { path: 'verifications', lazy: page(() => import('@/features/verification/pages/VerificationsPage')) },
              {
                path: 'verifications/:id',
                lazy: page(() => import('@/features/verification/pages/VerificationDetailPage')),
              },

              { path: 'complaints', lazy: page(() => import('@/features/complaints/pages/ComplaintsPage')) },
              {
                path: 'complaints/:id',
                lazy: page(() => import('@/features/complaints/pages/ComplaintDetailPage')),
              },

              { path: 'shipments', lazy: page(() => import('@/features/shipments/pages/ShipmentsPage')) },
              {
                path: 'shipments/suspicious',
                lazy: page(() => import('@/features/shipments/pages/SuspiciousShipmentsPage')),
              },
              { path: 'shipments/:id', lazy: page(() => import('@/features/shipments/pages/ShipmentDetailPage')) },

              { path: 'users', lazy: page(() => import('@/features/users/pages/UsersPage')) },
              { path: 'users/blocking', lazy: page(() => import('@/features/users/pages/UsersBlockingPage')) },
              { path: 'users/:id', lazy: page(() => import('@/features/users/pages/UserDetailPage')) },

              { path: 'deals', lazy: page(() => import('@/features/deals/pages/DealsPage')) },
              { path: 'deals/:id', lazy: page(() => import('@/features/deals/pages/DealDetailPage')) },

              { path: 'settings', lazy: page(() => import('@/features/settings/pages/SettingsPage')) },
              { path: 'outbox', lazy: page(() => import('@/features/outbox/pages/OutboxPage')) },
              { path: 'audit', lazy: page(() => import('@/features/audit/pages/AuditLogPage')) },

              { path: '*', Component: NotFoundPage },
            ],
          },
        ],
      },
    ],
  },
];

/** `basename` follows Vite's `base` (`VITE_BASE`, e.g. `/admin/`); empty at the domain root. */
export const router = createBrowserRouter(routes, { basename: ROUTER_BASENAME });
